import { createSlice } from "@reduxjs/toolkit";
import { apiFetch, getFullImageUrl } from "../api";
import { toast } from "react-hot-toast";
import { getFallbackImage } from "../utils/imageLoader";

// Initial state and reducers
const wishlistSlice = createSlice({
  name: "wishlist",
  initialState: {
    items: [],
    itemCount: 0,
  },
  reducers: {
    setWishlist: (state, action) => {
      state.items = action.payload || [];
      state.itemCount = state.items.length;
    },
    // Add product to wishlist if not already present
    addToWishlist: (state, action) => {
      const productToAdd = action.payload;
      const existingItem = state.items.find(item => item.id === productToAdd.id);
      if (!existingItem) {
        state.items.push(productToAdd);
        state.itemCount += 1;
      }
    },
    // Remove product from wishlist by ID
    removeFromWishlist: (state, action) => {
      const productIdToRemove = action.payload;
      state.items = state.items.filter(item => item.id !== productIdToRemove);
      state.itemCount = state.items.length;
    },
    clearWishlist: (state) => {
      state.items = [];
      state.itemCount = 0;
    }
  }
});
// Export actions and reducer
export const { setWishlist, addToWishlist, removeFromWishlist, clearWishlist } = wishlistSlice.actions;
export default wishlistSlice.reducer;

// Thunk actions
export const fetchUserWishlist = () => async (dispatch) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const data = await apiFetch('/api/wishlist/all');

    if (!data || !Array.isArray(data)) {
        dispatch(setWishlist([]));
        return;
    }
    // Filter out items with null products
    const validItems = data.filter(item => item.product !== null);

    // Transform data to include full image URLs and necessary fields
    const transformedData = validItems.map(item => {
        
        let firstImage = getFallbackImage();
        let allImages = [];
// Check if images exist and are in array format
        if (item.product.images && Array.isArray(item.product.images) && item.product.images.length > 0) {
            const firstImageData = item.product.images[0];
            firstImage = getFullImageUrl(typeof firstImageData === 'string' ? firstImageData : firstImageData?.url);
            allImages = item.product.images.map(imgData =>
                getFullImageUrl(typeof imgData === 'string' ? imgData : imgData?.url)
            );
        }

        return {
          ...item.product,
          image: firstImage,
          images: allImages,
          wishlistId: item._id,
          id: item.product._id
        };
    });

    // Update state with transformed wishlist data
    dispatch(setWishlist(transformedData));
  } catch (error) {
    console.error("Error fetching wishlist:", error);
    dispatch(setWishlist([]));
  }
};
// Add product to wishlist with optional notification
export const addProductToWishlist = (product, options = { showNotification: true }) => async (dispatch, getState) => {
  try {
    if (!localStorage.getItem('token')) {
      dispatch(addToWishlist(product));
      if (options.showNotification) {
        window.dispatchEvent(new CustomEvent('wishlistNotification', { detail: { type: 'add', item: product } }));
      }
      return;
    }
    // Prevent duplicate additions
    const currentItems = getState().wishlist.items;
    if (currentItems.some(item => item.id === product.id)) return;
    const payload = { product: product.id };
    const data = await apiFetch('/api/wishlist/add', { method: 'POST', body: payload });
    const newWishlistItemId = data?.wishlistItem?._id;
    if (!newWishlistItemId) {
        await dispatch(fetchUserWishlist());
        console.warn("Wishlist item ID missing in add response, refetched list.");
    } else {
        const newWishlistItem = { ...product, wishlistId: newWishlistItemId, id: product.id };
        dispatch(addToWishlist(newWishlistItem));
    }
    if (options.showNotification) {
      window.dispatchEvent(new CustomEvent('wishlistNotification', { detail: { type: 'add', item: product } }));
    }
  } catch (error) {
    // Handle errors based on notification preference
    if (options.showNotification) {
      toast.error(error.message || "Failed to add to wishlist.");
    } else {
      console.error("Silent wishlist add failed:", error);
    }
  }
};
// Remove product from wishlist with optional notification
export const removeProductFromWishlist = (product) => async (dispatch) => {
  try {
    const isGuest = !localStorage.getItem('token');
    if (isGuest) {
      dispatch(removeFromWishlist(product.id));
      window.dispatchEvent(new CustomEvent('wishlistNotification', { detail: { type: 'remove', item: product } }));
      return;
    }
    // Ensure wishlistId is available for removal
    const wishlistId = product.wishlistId;
    if (!wishlistId) {
      console.error("Missing wishlistId for removal.");
      toast.error("Error removing item. Please try again.");
      return;
    }
    // API call to remove from wishlist
    await apiFetch(`/api/wishlist/delete/${wishlistId}`, { method: 'DELETE' });
    dispatch(removeFromWishlist(product.id));
    // Trigger removal notification
    window.dispatchEvent(new CustomEvent('wishlistNotification', { detail: { type: 'remove', item: product } }));
  } catch (error) {
    toast.error(error.message || "Failed to remove from wishlist.");
  }
};